// DELEGATE. Every Routines read and write hook now lives in
// `@heidi/agents/data`; this file keeps its name, its full export surface and
// every positional signature, so the 17 production importers and the 25 suites
// that `jest.mock('@/features/workflows/hooks')` resolve unchanged.
//
// WHAT SURVIVES HERE IS EXACTLY WHAT A HEADLESS PACKAGE MAY NOT HOLD, and it is
// three things rather than a general reluctance to move:
//
//   1. THE READ GATE. `useAuthGatedEnabled(useIsWorkflowsEnabled())` is a
//      feature-flag read AND an auth read. It crosses as one `enabled`
//      parameter per hook, computed here, so a flag flip or a pre-auth boot
//      window still stops the query firing at exactly the moment it did before
//      (a pre-auth 401 logs the user out — that is what the gate is for).
//   2. ANALYTICS. `trackWorkflowActioned` / `trackWorkflowRunClicked` reach
//      app analytics, and the run one additionally probes the desktop RPA
//      bridge. They are passed as the package's app-reaction ports, by
//      reference, so each fires with the same properties at the same moment.
//   3. THE DESKTOP ATTACHMENT PROBE. `resolveWorkflowRunRequestExtras` lives
//      next to the roster client (`rpa/desktop-target`) because that probe
//      and the Tauri caps read are app couplings a headless package must not
//      import. It crosses as the `resolveRunRequestExtras` port; the decision
//      it feeds — omit both fields when it resolves empty — is request
//      behaviour and moved with the hook.
//
// THE TOASTS DID NOT SURVIVE HERE, and that is the visible change: all seven
// were static `intl.formatMessage` descriptors, so their copy travels with the
// mutations as `meta` and the app's one `MutationCache` subscriber renders it.
// This file imports no `toast` and formats no copy; `useIntl` appears only as
// a locale read for the catalog cache key.
import { useIsSessionRoutineRunsEnabled, useIsWorkflowsEnabled } from '@/features/flags';
import { resolveWorkflowRunRequestExtras } from '@/features/rpa/desktop-target/resolveWorkflowRunRequestExtras';
import {
  isCreditAllowanceExhausted,
  useAddWorkflowEventRule as useAddWorkflowEventRuleCore,
  useAllWorkflowRuns as useAllWorkflowRunsCore,
  useChatWorkflows as useChatWorkflowsCore,
  useConnectorWorkflowTemplates as useConnectorWorkflowTemplatesCore,
  useConnectorWorkflows as useConnectorWorkflowsCore,
  useCreateWorkflow,
  useDeleteWorkflow as useDeleteWorkflowCore,
  useDeleteWorkflowEventRule as useDeleteWorkflowEventRuleCore,
  useDeleteWorkflowSchedule as useDeleteWorkflowScheduleCore,
  useFetchLatestRunWithProject as useFetchLatestRunWithProjectCore,
  useInstallPrimaryForConnector as useInstallPrimaryForConnectorCore,
  useIsAuthenticated,
  useResolvableWorkflow as useResolvableWorkflowCore,
  useRoutineForProject as useRoutineForProjectCore,
  useRunAnyWorkflow as useRunAnyWorkflowCore,
  useRunWorkflow as useRunWorkflowCore,
  useSessionWorkflowRuns as useSessionWorkflowRunsCore,
  useSetWorkflowEventRules as useSetWorkflowEventRulesCore,
  useSetWorkflowSchedule as useSetWorkflowScheduleCore,
  useShareWorkflow as useShareWorkflowCore,
  useSystemWorkflows as useSystemWorkflowsCore,
  useToggleWorkflowEnabled as useToggleWorkflowEnabledCore,
  useToggleWorkflowSchedulePaused as useToggleWorkflowSchedulePausedCore,
  useUnshareWorkflow as useUnshareWorkflowCore,
  useUpdateWorkflow as useUpdateWorkflowCore,
  useWorkflow as useWorkflowCore,
  useWorkflowEventRules as useWorkflowEventRulesCore,
  useWorkflowRuns as useWorkflowRunsCore,
  useWorkflowSchedule as useWorkflowScheduleCore,
  useWorkflowSchedules as useWorkflowSchedulesCore,
  useWorkflowShortcut as useWorkflowShortcutCore,
  useWorkflowTemplates as useWorkflowTemplatesCore,
  useWorkflows as useWorkflowsCore,
  type RunWorkflowVariables,
} from '@heidi/agents/data';
import { invalidateCreditAllowance } from '@heidi/commerce/data';
import { useIntl } from 'react-intl';

import type { WorkflowListQuery } from './client';
import { useExpectSessionRoutineRuns } from './lib/useExpectSessionRoutineRuns';
import { WORKFLOW_KEYS } from './queryKeys';
import { trackWorkflowActioned, trackWorkflowRunClicked } from './workflowAnalytics';

export type { RunAnyWorkflowInput } from './workflowAnalytics';
export { WORKFLOW_KEYS };
export type { RunWorkflowVariables };

// Pure cache commands and pure derivations: no app input, so they are plain
// re-exports rather than wrappers.
export {
  getSessionRunsRefetchInterval,
  invalidateWorkflowLists,
  invalidateWorkflowRunQueries,
} from '@heidi/agents/data';

// `useCreateWorkflow` alone needed nothing at all — no gate (it is a write), no
// analytics, no toast — so it crosses as a re-export too.
export { useCreateWorkflow };

/** Flag on and session ready — blocks Work queries before auth settles (pre-auth 401 → logout). */
const useAuthGatedEnabled = (flagEnabled: boolean): boolean => {
  const isAuthenticated = useIsAuthenticated();
  return flagEnabled && isAuthenticated;
};

/**
 * The preset catalog is server-localised, so the package keys its cache by
 * the active locale. Which locale is active is app policy, composed here
 * like the read gate.
 */
const useActiveLocale = (): string => useIntl().locale;

/**
 * The analytics ports, wired once at module scope.
 *
 * Passed BY REFERENCE rather than wrapped in an arrow: the package fires them
 * with the properties it built, and a wrapper here would be a second place a
 * property could be added or dropped without a test noticing.
 */
const analyticsPorts = { onRoutineActioned: trackWorkflowActioned } as const;
const refreshCreditAllowanceOnExhausted = (error: unknown) => {
  if (isCreditAllowanceExhausted(error)) {
    void invalidateCreditAllowance();
  }
};

const runPorts = {
  onRunClicked: trackWorkflowRunClicked,
  onRunError: refreshCreditAllowanceOnExhausted,
  resolveRunRequestExtras: resolveWorkflowRunRequestExtras,
} as const;

// ── Queries ─────────────────────────────────────────────────────

export const useWorkflows = (
  query: WorkflowListQuery = {},
  { staleTime = 0, enabled = true }: { staleTime?: number; enabled?: boolean } = {}
) =>
  useWorkflowsCore({
    query,
    staleTime,
    enabled: useAuthGatedEnabled(useIsWorkflowsEnabled()) && enabled,
  });

/**
 * @deprecated Explore uses ``useWorkflowTemplates`` + ``workflowFromPreset``.
 * Kept for any leftover system-workflow callers.
 */
export const useSystemWorkflows = () =>
  useSystemWorkflowsCore({ enabled: useAuthGatedEnabled(useIsWorkflowsEnabled()) });

export const useWorkflow = (workflowId: string | undefined) =>
  useWorkflowCore({
    workflowId,
    enabled: useAuthGatedEnabled(useIsWorkflowsEnabled()),
  });

/** The Heidi-curated preset catalog shown in the Marketplace gallery. */
export const useWorkflowTemplates = ({ enabled = true }: { enabled?: boolean } = {}) =>
  useWorkflowTemplatesCore({
    enabled: useAuthGatedEnabled(useIsWorkflowsEnabled()) && enabled,
    locale: useActiveLocale(),
  });

/**
 * Presets bound to a Connected-apps provider (includes hidden integration ones).
 */
export const useConnectorWorkflowTemplates = (connector: string | undefined) =>
  useConnectorWorkflowTemplatesCore({
    connector,
    enabled: useAuthGatedEnabled(useIsWorkflowsEnabled()),
    locale: useActiveLocale(),
  });

/** Installed (including hidden) workflows for a Connected-apps provider. */
export const useConnectorWorkflows = (connector: string | undefined) =>
  useConnectorWorkflowsCore({
    connector,
    enabled: useAuthGatedEnabled(useIsWorkflowsEnabled()),
  });

/**
 * Resolve a split-view selection id: persisted workflow from the API, or a
 * marketplace preset adapted into the Workflow card/conversation shape.
 */
export const useResolvableWorkflow = (workflowId: string | undefined) =>
  useResolvableWorkflowCore({
    workflowId,
    enabled: useAuthGatedEnabled(useIsWorkflowsEnabled()),
    locale: useActiveLocale(),
  });

export const useWorkflowSchedule = (workflowId: string | undefined) =>
  useWorkflowScheduleCore({
    workflowId,
    enabled: useAuthGatedEnabled(useIsWorkflowsEnabled()),
  });

export const useWorkflowEventRules = (workflowId: string | undefined) =>
  useWorkflowEventRulesCore({
    workflowId,
    enabled: useAuthGatedEnabled(useIsWorkflowsEnabled()),
  });

export const useWorkflowRuns = (
  workflowId: string | undefined,
  options?: { limit?: number }
) =>
  useWorkflowRunsCore({
    workflowId,
    limit: options?.limit,
    enabled: useAuthGatedEnabled(useIsWorkflowsEnabled()),
  });

export const useAllWorkflowRuns = (
  workflowId?: string,
  options?: { enabled?: boolean }
) =>
  useAllWorkflowRunsCore({
    workflowId,
    // Both halves of the pre-carve `enabled` composed here: the app gate, and
    // the caller's opt-out that keeps the hook mounted without fetching.
    enabled: useAuthGatedEnabled(useIsWorkflowsEnabled()) && options?.enabled !== false,
  });

export const useSessionWorkflowRuns = (
  sessionId: string | undefined,
  options?: { pollWhileLive?: boolean }
) => {
  const pollWhileAwaitingRuns = useExpectSessionRoutineRuns(sessionId);
  return useSessionWorkflowRunsCore({
    sessionId,
    // A different flag from every other read hook, as before.
    enabled: useAuthGatedEnabled(useIsSessionRoutineRunsEnabled()),
    pollWhileAwaitingRuns,
    pollWhileLive: options?.pollWhileLive,
  });
};

/**
 * Imperatively resolve a routine's most recent openable run (one with a chat
 * project behind it). See the package hook for the cache-sharing contract.
 *
 * The gate here is the routines flag ALONE, NOT the auth-gated form — an
 * imperative `fetchQuery` has no `enabled` option, so the pre-carve hook read
 * `useIsWorkflowsEnabled()` directly and returned null without fetching.
 * Preserved verbatim; adding the auth read would change when it no-ops.
 */
export const useFetchLatestRunWithProject = () =>
  useFetchLatestRunWithProjectCore({ enabled: useIsWorkflowsEnabled() });

/**
 * Resolve the routine behind a Work project, when the project came from a
 * workflow run. See the package hook for the mapping and its scope guard.
 */
export const useRoutineForProject = (projectId: string | undefined) =>
  useRoutineForProjectCore({
    projectId,
    enabled: useAuthGatedEnabled(useIsWorkflowsEnabled()),
  });

/** Fetch schedules for multiple workflows in a single batch request. */
export const useWorkflowSchedules = (workflowIds: string[]) =>
  useWorkflowSchedulesCore({
    workflowIds,
    enabled: useAuthGatedEnabled(useIsWorkflowsEnabled()),
  });

/** Convenience: get all instructed (non-skill) workflows for the slash picker. */
export const useChatWorkflows = () =>
  useChatWorkflowsCore({ enabled: useAuthGatedEnabled(useIsWorkflowsEnabled()) });

// ── Mutations ───────────────────────────────────────────────────

export const useInstallPrimaryForConnector = () =>
  useInstallPrimaryForConnectorCore(analyticsPorts);

export const useUpdateWorkflow = (workflowId: string) =>
  useUpdateWorkflowCore({ workflowId, ...analyticsPorts });

/**
 * Persist a workflow's keyboard shortcut on the backend. Passing `''` clears it
 * (the backend maps `''` -> unset; do not send `null`).
 */
export const useWorkflowShortcut = (workflowId: string) =>
  useWorkflowShortcutCore({ workflowId, ...analyticsPorts });

export const useDeleteWorkflow = () => useDeleteWorkflowCore(analyticsPorts);

export const useToggleWorkflowEnabled = (workflowId: string) =>
  useToggleWorkflowEnabledCore({ workflowId, ...analyticsPorts });

export const useShareWorkflow = (workflowId: string) =>
  useShareWorkflowCore({ workflowId, ...analyticsPorts });

export const useUnshareWorkflow = (workflowId: string) =>
  useUnshareWorkflowCore({ workflowId, ...analyticsPorts });

export const useRunWorkflow = (workflowId: string) =>
  useRunWorkflowCore({ workflowId, ...runPorts });

/** Like `useRunWorkflow` but accepts the workflow ID at call time. */
export const useRunAnyWorkflow = () => useRunAnyWorkflowCore(runPorts);

// ── Schedule mutations ──────────────────────────────────────────

export const useSetWorkflowSchedule = (workflowId: string) =>
  useSetWorkflowScheduleCore({ workflowId, ...analyticsPorts });

export const useDeleteWorkflowSchedule = (workflowId: string) =>
  useDeleteWorkflowScheduleCore({ workflowId, ...analyticsPorts });

/**
 * Pause or resume the Temporal schedule linked to a workflow.
 * Pass ``true`` to pause, ``false`` to resume.
 */
export const useToggleWorkflowSchedulePaused = (workflowId: string) =>
  useToggleWorkflowSchedulePausedCore({ workflowId, ...analyticsPorts });

// ── Event rule mutations ────────────────────────────────────────

export const useAddWorkflowEventRule = (workflowId: string) =>
  useAddWorkflowEventRuleCore({ workflowId, ...analyticsPorts });

export const useDeleteWorkflowEventRule = (workflowId: string) =>
  useDeleteWorkflowEventRuleCore({ workflowId, ...analyticsPorts });

/**
 * Replace all event rules (delete existing, add new). Non-atomic; if the add
 * phase fails, the error toast warns the user.
 */
export const useSetWorkflowEventRules = (workflowId: string) =>
  useSetWorkflowEventRulesCore({ workflowId, ...analyticsPorts });

/** Convenience: re-export for consumers that need the Workflow type. */
export type { Workflow } from './types';
